import React, { useEffect, useState } from 'react';
import { Drawer, Descriptions, Table, Progress, Badge, Typography } from 'antd';
import { getOtaJobDetail, OtaJobDetail } from '@/api/ota';

const { Text } = Typography;

const statusMap: Record<string, { color: string; label: string }> = {
  pending: { color: 'default', label: '待升级' },
  offered: { color: 'blue', label: '已下发' },
  transferring: { color: 'cyan', label: '传输中' },
  verifying: { color: 'gold', label: '校验中' },
  success: { color: 'green', label: '成功' },
  failed: { color: 'red', label: '失败' },
  rejected: { color: 'volcano', label: '已拒绝' },
  canceled: { color: 'gray', label: '已取消' },
};

const terminal = ['success', 'failed', 'canceled'];

interface Props {
  open: boolean;
  jobId: string;
  onClose: () => void;
}

const JobDetailDrawer: React.FC<Props> = ({ open, jobId, onClose }) => {
  const [detail, setDetail] = useState<OtaJobDetail | null>(null);

  useEffect(() => {
    if (!open || !jobId) {
      setDetail(null);
      return;
    }
    let stopped = false;
    const fetchDetail = async () => {
      const resp = await getOtaJobDetail(jobId);
      if (stopped || !resp.success) return;
      setDetail(resp.result);
      if (terminal.includes(resp.result?.job?.status)) {
        clearInterval(timer);
      }
    };
    const timer = setInterval(fetchDetail, 2000);
    fetchDetail();
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [open, jobId]);

  const job = detail?.job;
  const tasks = detail?.tasks || [];

  return (
    <Drawer title="升级任务详情" width={720} open={open} onClose={onClose}>
      {job && (
        <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="任务ID">{job.id}</Descriptions.Item>
          <Descriptions.Item label="状态">
            {statusMap[job.status] ? (
              <>
                <Badge color={statusMap[job.status].color} />
                <Text className="ml5">{statusMap[job.status].label}</Text>
              </>
            ) : (
              job.status
            )}
          </Descriptions.Item>
          <Descriptions.Item label="设备总数">{job.totalDevices}</Descriptions.Item>
          <Descriptions.Item label="成功 / 失败">
            {job.successCount} / {job.failedCount}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Table
        rowKey="deviceId"
        size="small"
        dataSource={tasks}
        pagination={false}
        columns={[
          { title: '设备ID', dataIndex: 'deviceId', width: 140 },
          { title: '设备名称', dataIndex: 'deviceName', width: 120, render: (v) => v || '-' },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (v) => (statusMap[v] ? statusMap[v].label : v || '-'),
          },
          {
            title: '进度',
            dataIndex: 'progress',
            width: 160,
            render: (v, r) => <Progress percent={v} size="small" format={() => `${r.currentChunk}/${r.totalChunks}`} />,
          },
          {
            title: '失败原因',
            dataIndex: 'errorMessage',
            ellipsis: true,
            render: (v) => v || '-',
          },
        ]}
      />
    </Drawer>
  );
};

export default JobDetailDrawer;
